import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Field, TextArea, TextInput } from '../components/Field.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { VerticalTabs } from '../components/VerticalTabs.tsx';
import { AdminUsers } from './Users.tsx';
import { usePortalSettings } from '../lib/portalSettings.tsx';
import { useConfirm } from '../lib/confirm.tsx';
import { COMPANY_INFO_LABEL_DEFAULTS, type PortalSettings } from '@l2u/shared/schemas';
import { normalizeReminderDays } from '@l2u/shared/reminders';
import type { QuestionCategory } from '../lib/types.ts';
import { SETTINGS_TABS, resolveSettingsTab } from '../lib/settingsTabs.ts';

export function AdminSettings() {
  const qc = useQueryClient();
  const { reload } = usePortalSettings();
  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => adminApi.get<{ settings: Partial<PortalSettings> }>('/api/settings'),
  });

  const [form, setForm] = useState<Partial<PortalSettings>>({});
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (data) setForm({ ...data.settings });
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (input: Partial<PortalSettings>) => adminApi.put('/api/settings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void reload();
    },
    onError: (err) => setError(err),
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveSettingsTab(searchParams.get('tab'));
  const setActiveTab = (key: string) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', key);
        return p;
      },
      { replace: true },
    );

  if (isLoading) return <CenteredSpinner />;

  function set<K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function save() {
    setError(null);
    saveMutation.mutate(form);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Manage portal configuration</p>
      </div>
      <div className="flex gap-6">
        <VerticalTabs
          tabs={SETTINGS_TABS}
          active={activeTab}
          onSelect={setActiveTab}
          ariaLabel="Settings sections"
        />
        <div
          className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-6"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === 'users' && <AdminUsers />}
          {activeTab === 'content' && (
            <PortalContentTab
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
              error={error}
            />
          )}
          {activeTab === 'company-info' && (
            <VendorInfoLabelsPanel
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'contractor-types' && (
            <OptionListPanel
              title="Contractor Types"
              hint="These become the audience options on questionnaires and the choices on the new-vendor form."
              value={form.contractorTypeOptions ?? []}
              onChange={(list) =>
                set('contractorTypeOptions', list.length === 0 ? undefined : list)
              }
              itemLabel="contractor type"
              addLabel="Add contractor type"
              placeholder="e.g. Owner Driver"
              save={save}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'cluster-regions' && (
            <OptionListPanel
              title="Cluster Regions"
              hint="These become the choices for the Cluster Region dropdown on the new-vendor form and the vendor detail page."
              value={form.clusterRegionOptions ?? []}
              onChange={(list) =>
                set('clusterRegionOptions', list.length === 0 ? undefined : list)
              }
              itemLabel="cluster region"
              addLabel="Add cluster region"
              placeholder="e.g. Metro North"
              save={save}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'stations' && (
            <OptionListPanel
              title="Stations"
              hint="These become the choices for the Station dropdown on the new-vendor form and the vendor detail page."
              value={form.stationOptions ?? []}
              onChange={(list) => set('stationOptions', list.length === 0 ? undefined : list)}
              itemLabel="station"
              addLabel="Add station"
              placeholder="e.g. Sydney Depot"
              save={save}
              saving={saveMutation.isPending}
            />
          )}
          {activeTab === 'notifications' && (
            <NotificationsPanel
              form={form}
              set={set}
              save={save}
              saving={saveMutation.isPending}
              error={error}
            />
          )}
          {activeTab === 'question-categories' && <QuestionCategoriesPanel />}
        </div>
      </div>
    </div>
  );
}

function PortalContentTab({
  form,
  set,
  save,
  saving,
  error,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
  error: unknown;
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Portal Content</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <div className="max-w-2xl space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Invitation email welcome
        </h2>
        <Field label="Heading">
          <TextInput
            value={form.welcomeHeading ?? ''}
            onChange={(e) => set('welcomeHeading', e.target.value)}
          />
        </Field>
        <Field label="Message">
          <TextArea
            value={form.welcomeMessage ?? ''}
            onChange={(e) => set('welcomeMessage', e.target.value)}
          />
        </Field>

        <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-500">Legal</h2>
        <Field label="Terms & Conditions (Markdown)" hint="Markdown supported">
          <TextArea
            value={form.termsContent ?? ''}
            onChange={(e) => set('termsContent', e.target.value)}
          />
        </Field>
        <Field label="Privacy Policy (Markdown)" hint="Markdown supported">
          <TextArea
            value={form.privacyContent ?? ''}
            onChange={(e) => set('privacyContent', e.target.value)}
          />
        </Field>

        <ErrorMessage error={error} />
      </div>
    </div>
  );
}

function VendorInfoLabelsPanel({
  form,
  set,
  save,
  saving,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
}) {
  const labels = form.companyInfoLabels ?? {};

  function setLabel(key: keyof typeof COMPANY_INFO_LABEL_DEFAULTS, value: string) {
    const trimmed = value.trim();
    const next = { ...(form.companyInfoLabels ?? {}) };
    if (trimmed.length === 0) {
      delete (next as Record<string, unknown>)[key];
    } else {
      (next as Record<string, string>)[key] = trimmed;
    }
    set('companyInfoLabels', Object.keys(next).length === 0 ? undefined : next);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Vendor Information</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <p className="text-xs text-slate-500">
        Rename the labels shown on the admin invite form and the vendor&apos;s questionnaire. Leave
        blank to use the default label.
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(
          Object.entries(COMPANY_INFO_LABEL_DEFAULTS) as [
            keyof typeof COMPANY_INFO_LABEL_DEFAULTS,
            string,
          ][]
        ).map(([key, fallback]) => (
          <Field key={key} label={`${key} label`} hint={`Default: ${fallback}`}>
            <TextInput
              value={labels[key] ?? ''}
              placeholder={fallback}
              onChange={(e) => setLabel(key, e.target.value)}
            />
          </Field>
        ))}
      </div>
    </div>
  );
}

function OptionListPanel({
  title,
  hint,
  value,
  onChange,
  itemLabel,
  addLabel,
  placeholder,
  save,
  saving,
}: {
  title: string;
  hint: string;
  value: string[];
  onChange: (list: string[]) => void;
  itemLabel: string;
  addLabel: string;
  placeholder: string;
  save: () => void;
  saving: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <p className="text-xs text-slate-500">{hint}</p>
      <StringListEditor
        value={value}
        onChange={onChange}
        itemLabel={itemLabel}
        addLabel={addLabel}
        placeholder={placeholder}
        maxLength={120}
      />
    </div>
  );
}

function NotificationsPanel({
  form,
  set,
  save,
  saving,
  error,
}: {
  form: Partial<PortalSettings>;
  set: <K extends keyof PortalSettings>(key: K, value: PortalSettings[K]) => void;
  save: () => void;
  saving: boolean;
  error: unknown;
}) {
  const days = form.documentExpiryReminderDays ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Notifications</h2>
        <Btn onClick={save} loading={saving}>
          Save
        </Btn>
      </div>
      <p className="text-xs text-slate-500">
        Email the vendor a reminder this many days before an uploaded document expires. Add
        multiple values to send more than one reminder (e.g. 30, 20 and 10 days out). Remove
        every value to turn expiry reminders off. Until this is configured, the portal reminds
        once, 14 days out.
      </p>
      <StringListEditor
        value={days.map(String)}
        onChange={(list) =>
          set('documentExpiryReminderDays', normalizeReminderDays(list.map(Number)))
        }
        itemLabel="reminder"
        addLabel="Add reminder"
        placeholder="e.g. 30"
        maxLength={3}
      />
      <ErrorMessage error={error} />
    </div>
  );
}

function QuestionCategoriesPanel() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories?include_archived=1'),
  });

  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [error, setError] = useState<unknown>(null);

  const allCategories = data?.categories ?? [];
  const categories = allCategories.filter((c) => !c.deleted_at);
  const archived = allCategories.filter((c) => c.deleted_at);

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) => adminApi.post('/api/question-categories', input),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      adminApi.patch(`/api/question-categories/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      setEditingName('');
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
    },
    onError: (err) => setError(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/question-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => adminApi.post(`/api/question-categories/${id}/restore`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.post('/api/question-categories/reorder', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['question-categories'] }),
    onError: (err) => setError(err),
  });

  function move(id: string, dir: -1 | 1) {
    const ids = categories.map((c) => c.id);
    const idx = ids.indexOf(id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= ids.length) return;
    [ids[idx], ids[next]] = [ids[next]!, ids[idx]!];
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Question categories
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Categories are shared across all questionnaires and group questions into wizard steps.
          Parent/child links may cross categories — pick any question in the same questionnaire as a
          parent when setting up conditional logic.
        </p>
      </div>

      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          const trimmed = name.trim();
          if (!trimmed) return;
          createMutation.mutate({ name: trimmed });
        }}
      >
        <div className="flex-1">
          <Field label="Add category">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Vehicle safety"
            />
          </Field>
        </div>
        <Btn type="submit" loading={createMutation.isPending} disabled={!name.trim()}>
          Add
        </Btn>
      </form>

      {isLoading ? (
        <CenteredSpinner />
      ) : categories.length === 0 ? (
        <EmptyState
          title="No categories yet"
          description="Add a category above to start organising questions."
        />
      ) : (
        <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
          {categories.map((cat, i) => (
            <li key={cat.id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex flex-col">
                <button
                  onClick={() => move(cat.id, -1)}
                  disabled={i === 0}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(cat.id, 1)}
                  disabled={i === categories.length - 1}
                  className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  aria-label="Move down"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1">
                {editingId === cat.id ? (
                  <TextInput
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && editingName.trim()) {
                        updateMutation.mutate({ id: cat.id, name: editingName.trim() });
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <span className="text-sm text-slate-900">{cat.name}</span>
                )}
              </div>
              {editingId === cat.id ? (
                <>
                  <Btn
                    size="sm"
                    onClick={() => updateMutation.mutate({ id: cat.id, name: editingName.trim() })}
                    loading={updateMutation.isPending}
                    disabled={!editingName.trim()}
                  >
                    Save
                  </Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </Btn>
                </>
              ) : (
                <>
                  <Btn
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingId(cat.id);
                      setEditingName(cat.name);
                    }}
                  >
                    Rename
                  </Btn>
                  <Btn
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Archive category',
                            body: (
                              <>
                                Archive <span className="font-semibold">{cat.name}</span>? Its
                                questions stay in their questionnaires with all answers — it is just
                                removed from the category list and can be restored later.
                              </>
                            ),
                            confirmLabel: 'Archive',
                          })
                        )
                          deleteMutation.mutate(cat.id);
                      })();
                    }}
                  >
                    Archive
                  </Btn>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {archived.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Archived
          </h3>
          <div className="space-y-2">
            {archived.map((cat) => (
              <div
                key={cat.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
              >
                <span className="text-sm text-slate-500">{cat.name}</span>
                <Btn size="sm" variant="secondary" onClick={() => restoreMutation.mutate(cat.id)}>
                  Restore
                </Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      <ErrorMessage error={error} />
    </div>
  );
}
